import { Button } from '@lifeomic/chroma-react/components/Button';
import { FormBox } from '@lifeomic/chroma-react/components/FormBox';
import { generateLink, isCustomClientInUse } from './utils';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { messages } from './intlLabels';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Select, SelectOption } from '@lifeomic/chroma-react/components/Select';
import { Share } from '@lifeomic/chromicons';
import { ShareLinkButtonProps, SelectOptionType } from './types';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { useActiveAccountId } from '../../hooks/useActiveAccountId';
import { useActiveProjectId } from '../../hooks/useActiveProjectId';
import { useActiveProjectCohortId } from '../../hooks/useActiveProjectCohortId';
import { useIntl } from 'react-intl';
import { useNotify } from '../../hooks/useNotify';
import * as React from 'react';
import copy from 'copy-to-clipboard';
import { GlobalHotKeys } from 'react-hotkeys';
import { useSelector } from 'react-redux';
import { configSelectors } from '../../redux/modules/config';
import { authSelectors } from '../../redux/modules/auth';

const useStyles = makeStyles(theme => ({
  root: {
    color: theme.palette.black[500],
    '&:hover, &:focus': {
      color: theme.palette.primary.main,
    },
  },
}));

const KEY_MAP = {
  CREATE_LINK: 'c',
};

export const ShareLinkButton: React.FC<ShareLinkButtonProps> = ({
  color = 'inverse',
  createLink,
  placement = 'left',
  ...rootProps
}) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();

  const activeProjectId = useActiveProjectId();
  const activeAccountId = useActiveAccountId();
  const activeCohortId = useActiveProjectCohortId();
  const auth = useSelector(authSelectors.selectAuthState);
  const config = useSelector(configSelectors.selectConfigState);

  const [notify] = useNotify();

  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
  const [modalCopyChoice, setModalCopyChoice] =
    React.useState<SelectOptionType>('includeSSO');

  const handleClick = () => {
    if (!isModalOpen && isCustomClientInUse(config, auth)) {
      setIsModalOpen(true);
      return;
    }

    const generatedLink = createLink
      ? createLink({ activeAccountId, activeProjectId, activeCohortId })
      : generateLink({
          activeAccountId,
          activeProjectId,
          activeCohortId,
          url: window.location.href,
        });

    if (generatedLink) {
      copy(generatedLink);
      notify(formatMessage(messages.notification), 'info');
    } else {
      notify(formatMessage(messages.error), 'error');
    }
  };

  const handleCreateLinkModalClick = () => {
    if (isCustomClientInUse(config, auth) && modalCopyChoice === 'open') {
      // Since we are using a custom client for login, we don't
      // know what their client may be, so we will split the host
      // and remove the first item (e.g., 'lifeomic.apps.' will cutout 'lifeomic')
      // and then we will join them back up
      const parts = location.host.split('.');
      parts.shift();

      copy(
        generateLink({
          activeAccountId,
          activeProjectId,
          activeCohortId,
          url: window.location.href.replace(
            window.location.host,
            `${parts.join('.')}`,
          ),
        }),
      );
    } else {
      copy(
        generateLink({
          activeAccountId,
          activeProjectId,
          activeCohortId,
          url: window.location.href,
        }),
      );
    }

    notify(formatMessage(messages.notification), 'info');
  };

  const HANDLERS = {
    CREATE_LINK: handleClick,
  };

  return (
    <GlobalHotKeys keyMap={KEY_MAP} handlers={HANDLERS} allowChanges={true}>
      <div>
        <Tooltip placement={placement} title={formatMessage(messages.tooltip)}>
          <IconButton
            aria-label={formatMessage(messages.tooltip)}
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            className={color === 'default' && classes.root}
            color={color}
            size={0}
            icon={Share}
            onClick={handleClick}
            {...rootProps}
          />
        </Tooltip>
        <Modal
          actions={
            <>
              <Button variant="text" onClick={() => setIsModalOpen(false)}>
                {formatMessage(messages.modalCancel)}
              </Button>
              <Button
                onClick={() => {
                  handleCreateLinkModalClick();
                  setIsModalOpen(false);
                }}
              >
                {formatMessage(messages.modalCopy)}
              </Button>
            </>
          }
          title={formatMessage(messages.modalTitle)}
          isOpen={isModalOpen}
          onDismiss={() => setIsModalOpen(false)}
        >
          <FormBox>
            <Text size="subbody">{formatMessage(messages.modalBody)}</Text>
            <Select
              label={formatMessage(messages.modalSelectLabel)}
              value={modalCopyChoice}
              onChange={v => setModalCopyChoice(v as SelectOptionType)}
              fullWidth
            >
              <SelectOption
                title={formatMessage(messages.modalSelectIncludeSSO)}
                subtitle={formatMessage(messages.modalSelectIncludeSSOHelp)}
                value="includeSSO"
              />
              <SelectOption
                title={formatMessage(messages.modalSelectMakeOpenLink)}
                subtitle={formatMessage(messages.modalSelectMakeOpenLinkHelp)}
                value="open"
              />
            </Select>
          </FormBox>
        </Modal>
      </div>
    </GlobalHotKeys>
  );
};
